<script setup lang="ts">
import { computed } from "vue";
import type { ScreenTimePoint } from "@/types/protocol";
import { byCategoryOrder, categoryColor, OTHER_COLOR, UNTRACKED } from "@/utils/categories";
import { formatDuration } from "@/utils/format";

const props = defineProps<{
  days: readonly ScreenTimePoint[];
  /** Gemeinsame Obergrenze aller Benutzer - so sind die Reihen untereinander vergleichbar. */
  scaleSeconds: number;
}>();

const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

interface Segment {
  category: string;
  color: string;
  share: number;
  label: string;
}

interface Bar {
  date: string;
  height: number;
  weekend: boolean;
  today: boolean;
  label: string;
  segments: Segment[];
}

const today = new Date().toLocaleDateString("sv-SE"); // sv-SE liefert YYYY-MM-DD

const bars = computed<Bar[]>(() =>
  props.days.map((day) => {
    const date = new Date(`${day.date}T00:00:00`);
    const weekday = date.getDay();
    const dayLabel = `${weekdays[weekday]} ${date.getDate()}.${date.getMonth() + 1}.`;

    // Zeit ohne Kategorie (Tage vor der Anwendungserfassung) bekommt ein eigenes,
    // neutrales Segment - sonst behauptete der Stapel, der Tag sei kuerzer gewesen.
    const known = day.categories.reduce((sum, category) => sum + category.seconds, 0);
    const untracked = Math.max(0, day.seconds - known);
    const parts = [
      ...byCategoryOrder(day.categories).map((category) => ({
        category: category.category,
        seconds: category.seconds,
        color: categoryColor(category.category),
      })),
      ...(untracked > 1 ? [{ category: UNTRACKED, seconds: untracked, color: OTHER_COLOR }] : []),
    ];

    return {
      date: day.date,
      // Minimal sichtbarer Stummel, sobald überhaupt Zeit gebucht ist.
      height: day.seconds > 0 ? Math.max(3, (day.seconds / Math.max(1, props.scaleSeconds)) * 100) : 0,
      weekend: weekday === 0 || weekday === 6,
      today: day.date === today,
      label: `${dayLabel} · ${day.seconds > 0 ? formatDuration(day.seconds) : "nichts"}`,
      segments: parts.map((part) => ({
        category: part.category,
        color: part.color,
        share: (part.seconds / Math.max(1, day.seconds)) * 100,
        label: `${dayLabel} · ${part.category}: ${formatDuration(part.seconds)}`,
      })),
    };
  }),
);
</script>

<template>
  <div class="chart" role="img" :aria-label="`Verlauf: ${bars.map((b) => b.label).join(', ')}`">
    <div v-for="bar in bars" :key="bar.date" class="slot" :class="{ weekend: bar.weekend, today: bar.today }">
      <div class="col" :style="{ height: `${bar.height}%` }" :title="bar.label">
        <span
          v-for="segment in bar.segments"
          :key="segment.category"
          class="seg"
          :style="{ height: `${segment.share}%`, background: segment.color }"
          :title="segment.label"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * Höhe mit Bedacht: ein Tag zerfällt in bis zu neun Segmente, und jedes braucht neben
 * seiner Mindesthöhe noch 2px Abstand. Bei 84px fraßen die kleinen Kategorien den
 * Balken auf - die großen sahen dadurch kleiner aus, als sie waren.
 */
.chart {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 112px;
  padding-top: 4px;
}

.slot {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  /* Leere Tage bleiben als schwache Spur sichtbar. */
  background: linear-gradient(var(--line), var(--line)) bottom / 100% 1px no-repeat;
}

/* Wochenende und heute liegen jetzt im Hintergrund der Spalte: die Balkenfarbe
   gehört der Kategorie und darf nicht zwei Dinge gleichzeitig bedeuten. */
.slot.weekend {
  background:
    linear-gradient(var(--line), var(--line)) bottom / 100% 1px no-repeat,
    color-mix(in srgb, var(--panel2) 60%, transparent);
}

.slot.today {
  background:
    linear-gradient(var(--accent), var(--accent)) bottom / 100% 2px no-repeat,
    color-mix(in srgb, var(--accent) 10%, transparent);
}

.col {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  /* Marken bleiben schmal - der Rest des Platzes ist Luft. */
  max-width: 22px;
  transition: height 0.2s;
}

.seg {
  display: block;
  width: 100%;
  /* 3px, damit unter dem 2px-Abstand noch etwas Farbe übrig bleibt. */
  min-height: 3px;
}

/*
 * Die 2px Abstand zwischen zwei Segmenten sind ein durchsichtiger Rand *innerhalb*
 * der Segmenthöhe (box-sizing: border-box) - mit flex-gap würde der Balken um die
 * Abstände wachsen und die Höhe wäre gelogen. Das unterste Segment sitzt ohne Rand
 * auf der Grundlinie.
 */
.seg:not(:first-child) {
  border-bottom: 2px solid transparent;
  background-clip: padding-box;
}

.seg:last-child {
  border-radius: 4px 4px 0 0;
}

@media (prefers-reduced-motion: reduce) {
  .col {
    transition: none;
  }
}
</style>
